import { getFitOnHost } from 'apis/formatUrl'
import { getDeviceType } from 'utils/basic'
import { getAutoLoginParams } from 'utils/basic'
import { getUserEmail } from 'utils/authLocalStorage'
import { careReturnUrl, currentReturnTo, isResumableReturnTo } from './returnTo'

export const IgnoreFrom = ['iOS', 'Android']

// `reason`: a CareReason, for an msk fail (CARE-160).
export const onCareFlowFinished = ({
  sku,
  success,
  programId,
  from,
  fitonUserId,
  reason,
} = {}) => {
  switch (sku) {
    case 'msk':
      rehabCareFlowFinished(success, programId, from, fitonUserId, sku, reason)
      break
    case 'wlc':
      wlcCareFlowFinished(success, programId, from, fitonUserId, sku)
      break
    default:
      careFlowFinished(sku, success, programId, from, fitonUserId)
      break
  }
}

const careFlowFinished = (sku, success, programId, from, fitonUserId) => {
  window.close()
  const deviceType = getDeviceType()
  if (deviceType === 'Android' || deviceType === 'iOS') {
    try {
      if (window.webkit) {
        window.webkit.messageHandlers?.careFlowFinished?.postMessage({
          sku,
          programId,
          success,
        })
      } else {
        window.Android?.careFlowFinished?.(
          JSON.stringify({
            sku,
            programId,
            success,
          })
        )
      }
    } catch (e) {
      console.error(e)
    }
  }
  if (!IgnoreFrom.includes(from)) {
    if (sku === 'mca') {
      window.location.href = `${getFitOnHost()}${
        programId
          ? `?r=program/detail/${programId}&${getAutoLoginParams({ fitOnUserId: fitonUserId, fitOnEmail: getUserEmail() })}`
          : `?${getAutoLoginParams({ fitOnUserId: fitonUserId, fitOnEmail: getUserEmail() })}`
      }`
    } else {
      window.location.href = `${getFitOnHost()}${
        programId ? `?r=program/detail/${programId}` : ''
      }`
    }
  }
}

// Where the web flow goes when a rehab onboarding ends. With an allowed `returnTo` on the link (CARE-160),
// back to that FitOn web page with the result, except that a pass from a list page lands on the program
// the flow just enrolled the member in. Otherwise the old links. `reason` is a CareReason on a fail.
export const rehabWebReturnUrl = ({
  success,
  programId,
  fitonUserId,
  sku,
  reason,
  search,
}) => {
  if (sku === 'mca') {
    return `${getFitOnHost()}${
      programId
        ? `?r=program/detail/${programId}&${getAutoLoginParams({ fitOnUserId: fitonUserId, fitOnEmail: getUserEmail() })}`
        : `?${getAutoLoginParams({ fitOnUserId: fitonUserId, fitOnEmail: getUserEmail() })}`
    }`
  }
  const returnTo = currentReturnTo(search)
  if (returnTo) {
    const path =
      success && programId && !isResumableReturnTo(returnTo)
        ? `/program/detail/${programId}`
        : returnTo
    return careReturnUrl({
      host: getFitOnHost(),
      returnTo: path,
      success,
      reason,
    })
  }
  return `${getFitOnHost()}${programId ? `?r=program/detail/${programId}` : ''}`
}

// A web Close that goes back to FitOn web: only with an allowed `returnTo`. A legacy link has none, and its
// fallback is the bare app-link host, which lands on a 404 in desktop browsers.
export const canCloseToFitOnWeb = ({ sku, from }) =>
  sku === 'msk' && !IgnoreFrom.includes(from) && Boolean(currentReturnTo())

export const rehabCareFlowFinished = (
  success,
  programId,
  from,
  fitonUserId,
  sku,
  reason
) => {
  window.close()
  const deviceType = getDeviceType()
  if (deviceType === 'Android' || deviceType === 'iOS') {
    try {
      if (window.webkit) {
        window.webkit.messageHandlers?.rehabCareFlowFinished?.postMessage({
          programId,
          success,
        })
      } else {
        window.Android?.rehabCareFlowFinished?.(
          JSON.stringify({
            programId,
            success,
          })
        )
      }
    } catch (e) {
      console.error(e)
    }
  }
  if (!IgnoreFrom.includes(from)) {
    window.location.href = rehabWebReturnUrl({
      success,
      programId,
      fitonUserId,
      sku,
      reason,
    })
  }
}

export const wlcCareFlowFinished = (
  success,
  programId,
  from,
  fitonUserId,
  sku
) => {
  window.close()
  const deviceType = getDeviceType()
  if (deviceType === 'Android' || deviceType === 'iOS') {
    try {
      if (window.webkit) {
        window.webkit.messageHandlers?.openProgramDetail?.postMessage({
          programId,
          success,
        })
      } else {
        window.Android?.openProgramDetail?.(
          JSON.stringify({
            programId,
            success,
          })
        )
      }
    } catch (e) {
      console.error(e)
    }
  }

  if (!IgnoreFrom.includes(from)) {
    if (sku === 'mca') {
      window.location.href = `${getFitOnHost()}${
        programId
          ? `?r=program/detail/${programId}&${getAutoLoginParams({ fitOnUserId: fitonUserId, fitOnEmail: getUserEmail() })}`
          : `?${getAutoLoginParams({ fitOnUserId: fitonUserId, fitOnEmail: getUserEmail() })}`
      }`
    } else {
      window.location.href = `${getFitOnHost()}${
        programId ? `?r=program/detail/${programId}` : ''
      }`
    }
  }
}

// Rehab Care Flow Update Background for mobile webview
export const rehabCareFlowUpdateBackground = (showWelcome) => {
  const deviceType = getDeviceType()
  if (deviceType === 'Android' || deviceType === 'iOS') {
    try {
      if (window.webkit) {
        window.webkit.messageHandlers?.rehabCareFlowUpdateBackground?.postMessage(
          showWelcome
        )
      } else {
        window.Android?.rehabCareFlowUpdateBackground?.(
          JSON.stringify(showWelcome)
        )
      }
    } catch (e) {
      console.error(e)
    }
  }
}

export const onCareFlowClosed = ({ sku }) => {
  switch (sku) {
    case 'msk':
      rehabCareFlowClosed()
      break
    default:
      careFlowClosed(sku)
      break
  }
}

const careFlowClosed = (sku) => {
  const deviceType = getDeviceType()
  if (deviceType === 'Android' || deviceType === 'iOS') {
    try {
      if (window.webkit) {
        window.webkit.messageHandlers?.careFlowClosed?.postMessage({ sku })
      } else {
        window.Android?.careFlowClosed?.(JSON.stringify({ sku }))
      }
    } catch (e) {
      console.error(e)
    }
  }
  if (window.isDev) {
    console.log('careFlowClosed', sku)
  }
}

const rehabCareFlowClosed = () => {
  const deviceType = getDeviceType()
  if (deviceType === 'Android' || deviceType === 'iOS') {
    try {
      if (window.webkit) {
        window.webkit.messageHandlers?.rehabCareFlowClosed?.postMessage('close')
      } else {
        window.Android?.rehabCareFlowClosed?.()
      }
    } catch (e) {
      console.error(e)
    }
  }
  if (window.isDev) {
    console.log('rehabCareFlowClosed')
  }
}
